import { useTranslationApp } from "../../../../../lib/i18next";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {
  FRIDAY,
  MONDAY,
  SATURDAY,
  SUNDAY,
  THURSDAY,
  TUESDAY,
  WEDNESDAY,
} from "../../../../../i18n/keysTranslations";

const sxCutText = (maxLines = 2) => ({
  whiteSpace: "normal",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: maxLines,
  WebkitBoxOrient: "vertical",
});

// Days of the week for schedule display
const daysOfWeek = [
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
  SUNDAY,
];

// Helper to format minute to HH:MM
const formatMinute = (minute) => {
  const hours = Math.floor(minute / 60);
  const minutes = minute % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

const ScheduleDisplay = ({ scheduleValue, emptyValue: EMPTY_VALUE }) => {
  const { t } = useTranslationApp();

  if (!scheduleValue || scheduleValue === EMPTY_VALUE) {
    return <Typography variant="body2">{EMPTY_VALUE}</Typography>;
  }

  let schedule;
  try {
    // Parse the schedule value
    if (typeof scheduleValue === "string") {
      schedule = JSON.parse(scheduleValue);
    } else {
      schedule = scheduleValue;
    }
  } catch {
    return <Typography variant="body2">{String(scheduleValue)}</Typography>;
  }

  if (!schedule || !schedule.intervals || !Array.isArray(schedule.intervals)) {
    return <Typography variant="body2">{String(scheduleValue)}</Typography>;
  }

  // Group intervals by days for better display
  const dayIntervals = Array(7)
    .fill(null)
    .map(() => []);

  schedule.intervals.forEach((interval) => {
    if (interval.days && Array.isArray(interval.days)) {
      interval.days.forEach((day) => {
        if (day >= 0 && day < 7) {
          dayIntervals[day].push({
            start: interval.startMinute,
            end: interval.endMinute,
          });
        }
      });
    }
  });

  // Format intervals for each day
  const formattedIntervals = dayIntervals
    .map((intervals, dayIdx) => {
      if (intervals.length === 0) return null;

      const formattedTimes = intervals
        .map(({ start, end }) => `${formatMinute(start)}–${formatMinute(end)}`)
        .join(", ");

      return {
        day: t(daysOfWeek[dayIdx]),
        times: formattedTimes,
      };
    })
    .filter(Boolean);

  if (formattedIntervals.length === 0) {
    return <Typography variant="body2">{EMPTY_VALUE}</Typography>;
  }

  return (
    <Box>
      {formattedIntervals.map((item, index) => (
        <Box
          key={index}
          sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
        >
          <Typography
            component="span"
            variant="body2"
            sx={{
              fontWeight: 600,
              width: 80,
              ...sxCutText(1),
            }}
          >
            {item.day}:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              wordBreak: "break-word",
              ...sxCutText(1),
            }}
          >
            {item.times}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default ScheduleDisplay;
